/*
 * Copyright (c) 2020. American Express Company
 */

// third-party
import _ from 'lodash';
import React, { Component, ReactComponentElement } from 'react';
import { ChronoUnit } from '@js-joda/core';

// models and services
import { CardModel } from 'src/types/card.model';
import FormatService from '../../services/format.service';
import InsuranceService, { LifeInsuranceEstimate } from '../../services/insurance.service';

// components
import { FieldControl, Label } from '@americanexpress/dls-react';
import Anchor from '../atomic/atoms/anchor/Anchor';
import BwSlider from '../bw-slider/BwSlider';
import Dropdown from '../menus/Dropdown';
import GoalDrawer from './GoalDrawer';
import InfoCallout from '../callout/InfoCallout';
import LiteralsService from '../../services/literals/LiteralsService';
import Output from '../atomic/molecules/output/Output';

// icons and styles
import { ReactComponent as ShieldIcon } from 'src/bw/goal/shield.svg';
import { HELP_DOMAIN } from 'src/utils/constants';

export default class LifeInsuranceDrawer extends Component<any, LifeInsuranceEstimate> {
    public override state: LifeInsuranceEstimate;
    private lookupTimer!: number;

    /// ////////////////
    // Onced Methods //
    /// ////////////////
    private readonly handleDeleteOnce: () => void = _.once(async () => {
        this.cancelEstimateFetch();
        await this.props.handleDeleteCard(this.props.drawerProps.card);
    });

    private readonly handleUpdateOnce: () => void = _.once(async () => {
        this.cancelEstimateFetch();
        this.saveEstimateToCardAttributes();
        this.updateEndDate();
        this.props.handleUpdateCard();
        this.props.handleBack();
    });

    constructor(props: any) {
        super(props);
        this.state = LifeInsuranceDrawer.getStateFromCardAttributes(props);
    }

    private static getStateFromCardAttributes(props: any): LifeInsuranceEstimate {
        const attributes = props.drawerProps.card.attributes;
        const estimate: LifeInsuranceEstimate = new LifeInsuranceEstimate();

        // Get the attribute from the card or default to what the Estimate constructor has
        const getAttribute = (name: string): any => _.get(attributes, name, _.get(estimate, name));
        if (props.user.dob) {
            estimate.age = props.user.dob.until(props.drawerProps.card.startDate, ChronoUnit.YEARS);
        } else {
            estimate.age = 40;
        }

        estimate.gender = getAttribute('gender');
        estimate.health = getAttribute('health');
        estimate.termLength = Number(getAttribute('termLength'));
        estimate.coverageAmount = Number(getAttribute('coverageAmount'));
        return estimate;
    }

    public override componentDidUpdate(prevProps: Readonly<any>): void {
        const startDateChanged: boolean =
            this?.props?.drawerProps?.card.startDate !== prevProps?.drawerProps?.card?.startDate;
        if (this?.props?.drawerProps?.card?.id !== prevProps?.drawerProps?.card?.id || startDateChanged) {
            const estimate: LifeInsuranceEstimate = LifeInsuranceDrawer.getStateFromCardAttributes(this.props);
            this.setState(estimate);
            if (startDateChanged) this.changedEstimateField(estimate.age, 'age');
        }
    }

    public override componentWillUnmount(): void {
        this.cancelEstimateFetch();
    }

    public override render(): ReactComponentElement<any> {
        const drawerProps: any = this.props.drawerProps;
        const card: CardModel = drawerProps.card;

        const monthly: number = card.attributes.annualPremium / 12;
        const age: string = this.props.user.dob ? this.props.user.dob.until(card.startDate, ChronoUnit.YEARS) : '?';
        const startMonth: string = drawerProps.card.startDate.month().toString().toLowerCase();
        const startDate: string = `${
            startMonth.charAt(0).toUpperCase() + startMonth.slice(1)
        } ${drawerProps.card.startDate.year()}`;

        return (
            <GoalDrawer
                drawerProps={drawerProps}
                handleBack={this.props.handleBack}
                handleUpdateCard={this.props.handleUpdateCard}
                handleChangeCard={this.props.handleChangeCard}
                handleDeleteCard={this.handleDeleteOnce}
                showLoader={this.props.showLoader}
                hideLoader={this.props.hideLoader}
                icon={<ShieldIcon />}
                introContent={LiteralsService.getLiteral('LifeInsuranceDrawer.intro')}
                cashFlow={this.props.user.cashFlow}
                handleChangeCardDate={this.props.handleChangeCardDate.bind(this)}
                learnMoreContent={'Learn more about life insurance'}
                learnMoreUrl={HELP_DOMAIN ? HELP_DOMAIN + '/articles/life-insurance' : null}
                bottomButtonUpdate={this.handleUpdateOnce}
                datePickerCalloutContent={`You'll be age ${age} in ${startDate}`}
            >
                <FieldControl>
                    <Label>Gender</Label>
                    <Dropdown
                        value={this.state.gender}
                        onChange={(e) => this.changedEstimateField(e.target.value, 'gender')}
                        fullWidth={true}
                    >
                        <option value="M">Male</option>
                        <option value="F">Female</option>
                        <option value="O">Other</option>
                    </Dropdown>
                </FieldControl>

                <FieldControl>
                    <Label>Health</Label>
                    <Dropdown
                        value={this.state.health}
                        onChange={(e) => this.changedEstimateField(e.target.value, 'health')}
                        fullWidth={true}
                    >
                        <option value="SUBSTANDARD">Poor</option>
                        <option value="STANDARD">Average</option>
                        <option value="PREFERRED">Good</option>
                        <option value="PREFERRED_PLUS">Excellent</option>
                    </Dropdown>
                </FieldControl>

                <FieldControl>
                    <Label>Term length</Label>
                    <Dropdown
                        value={this.state.termLength}
                        onChange={(e) => this.changedEstimateField(Number(e.target.value), 'termLength')}
                        fullWidth={true}
                    >
                        <option value="10">10 Years</option>
                        <option value="20">20 Years</option>
                        <option value="30">30 Years</option>
                    </Dropdown>
                </FieldControl>

                <div className="drawerSection">
                    <BwSlider
                        id="slider"
                        label="Coverage amount"
                        value={this.state.coverageAmount}
                        formattedValue={FormatService.formatCompactMoney(this.state.coverageAmount)}
                        step={100000}
                        min={100000}
                        max={2000000}
                        onChange={(value: any) => this.changedEstimateField(Number(value), 'coverageAmount')}
                        inputToggleText="Add a precise amount"
                    />
                    <InfoCallout noMargin>
                        Not sure how much coverage you'll need?{' '}
                        <Anchor
                            target="_blank"
                            rel="noopener noreferrer"
                            href={`${HELP_DOMAIN}/articles/life-insurance`}
                        >
                            Read about how coverage is calculated
                        </Anchor>
                    </InfoCallout>
                </div>

                <Output
                    title="Estimated monthly payment"
                    main={monthly > 0 ? FormatService.format(monthly) : 'Coverage unlikely'}
                    subtext={monthly > 0 ? `or ${FormatService.format(card.attributes.annualPremium)} per year` : null}
                />
            </GoalDrawer>
        );
    }

    public updateEndDate(): void {
        const card: CardModel = this.props.drawerProps.card;
        const term = Number(card.attributes.termLength);
        card.endDate = card.startDate.plusYears(term);
    }

    private changedEstimateField(value: string | Number, field: string): boolean {
        // Reject characters in numeric fields by returning false
        if (_.isNumber(value) && !isFinite(value)) {
            return false;
        } else {
            this.setState(_.set(this.state, field, value));
            if (LifeInsuranceEstimate.isValid(this.state)) {
                this.cancelEstimateFetch();
                this.lookupTimer = window.setTimeout(() => {
                    void this.fetchEstimate.bind(this);
                }, 750);
            }
            return true;
        }
    }

    private cancelEstimateFetch(): void {
        if (this.lookupTimer) {
            clearTimeout(this.lookupTimer);
        }
    }

    private async fetchEstimate(): Promise<void> {
        const annualPremium: number = await InsuranceService.getLifeInsuranceEstimate(this.state);
        this.props.handleChangeCardAttribute(this.props.drawerProps.card, annualPremium, 'annualPremium', 'currency');
    }

    private saveEstimateToCardAttributes(): void {
        _.assign(this.props.drawerProps.card.attributes, this.state);
    }
}
